import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { X, Sparkles, Paperclip, Camera, Image as ImageIcon, ChevronRight } from "lucide-react";
import { api } from "../api/client";
import { Button, Field, IconButton } from "../components/UIKit";
import { pickImage } from "../lib/imagePicker";
import { INTERVENTION_TYPES, DOCUMENT_TYPES, RULES_LABELS } from "../lib/fleet";

export default function InterventionNew() {
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const vehicleId = params.get("vehicle_id");
  const isCustom = params.get("custom") === "1";
  const lockedCustomTypeId = params.get("custom_type_id") || null;
  const lockedCustomTitle = params.get("custom_title") || "";
  const initialType = lockedCustomTypeId
    ? "custom_maintenance"
    : isCustom
      ? "autre_document"
      : (params.get("type") && [...INTERVENTION_TYPES, "autre_document"].includes(params.get("type")))
        ? params.get("type")
        : "vidange";

  const [type, setType] = useState(initialType);
  const [customTypeId, setCustomTypeId] = useState(lockedCustomTypeId);
  const [customTypes, setCustomTypes] = useState([]);
  const [title, setTitle] = useState("");
  const [date, setDate] = useState(new Date().toISOString().substring(0, 10));
  const [expirationDate, setExpirationDate] = useState("");
  const [mileage, setMileage] = useState(params.get("km") || "");
  const [notes, setNotes] = useState("");
  const [amount, setAmount] = useState("");
  const [garage, setGarage] = useState("");
  const [photo, setPhoto] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const isDocument = isCustom || DOCUMENT_TYPES.includes(type);
  const isMaintenance = !isDocument;
  const isCustomMaintenance = type === "custom_maintenance";
  const activeCustomLabel = customTypes.find((c) => c.id === customTypeId)?.title || lockedCustomTitle;
  const screenTitle = isCustom
    ? "Nouveau document"
    : isDocument
      ? RULES_LABELS[type]
      : isCustomMaintenance
        ? activeCustomLabel || "Nouvel entretien"
        : "Nouvelle intervention";

  useEffect(() => {
    if (isDocument || lockedCustomTypeId) return;
    (async () => {
      try {
        const { data } = await api.get(`/vehicles/${vehicleId}`);
        setCustomTypes((data?.custom_maintenance || []).map((c) => ({ id: c.id, title: c.title })));
      } catch { /* ignore */ }
    })();
  }, [vehicleId, isDocument, lockedCustomTypeId]);

  const runScan = async () => {
    // Redirect to /scan/new which drives the camera picker through
    // CameraCaptureSheet + getUserMedia. Bypasses the file-picker path
    // that Chrome on Android was hijacking to Google Photos.
    const qs = new URLSearchParams();
    if (vehicleId) qs.set("vehicle_id", vehicleId);
    navigate(`/scan/new${qs.toString() ? `?${qs.toString()}` : ""}`);
  };

  const attachPhoto = async () => {
    const img = await pickImage();
    if (img) setPhoto(img);
  };

  const onSubmit = async (e) => {
    e?.preventDefault?.();
    if (loading) return;
    setError(null);
    if (isCustom && !title.trim()) return setError("Le titre du document est requis");
    if (isCustomMaintenance && !customTypeId) return setError("Aucun entretien personnalisé sélectionné");
    setLoading(true);
    try {
      await api.post("/interventions", {
        vehicle_id: vehicleId,
        type,
        date,
        mileage: mileage ? parseInt(mileage) : null,
        notes: notes || null,
        amount: amount ? parseFloat(amount) : null,
        garage: garage || null,
        invoice_photo: photo,
        title: isCustom ? title.trim() : null,
        expiration_date: expirationDate || null,
        custom_type_id: isCustomMaintenance ? customTypeId : null,
      });
      toast.success(
        isCustom
          ? "Document ajouté"
          : isDocument
            ? "Document enregistré"
            : isCustomMaintenance
              ? "Entretien enregistré"
              : "Intervention enregistrée"
      );
      navigate(-1);
    } catch (e) {
      setError(e?.response?.data?.detail || "Enregistrement impossible.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ maxWidth: 720, margin: "0 auto", paddingBottom: 100 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "20px 20px 12px" }}>
        <IconButton testID="intervention-back" icon={<X size={22} />} onClick={() => navigate(-1)} ariaLabel="Fermer" />
        <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>{screenTitle}</h2>
        <div style={{ width: 40 }} />
      </div>

      <form onSubmit={onSubmit} style={{ padding: "0 20px" }}>
        {isMaintenance && (
          <div style={{ position: "relative", marginBottom: 22 }}>
            <span className="mf-pulse-glow" style={{
              position: "absolute", inset: -3, borderRadius: 23,
              border: "2px solid var(--mf-accent)", pointerEvents: "none",
            }} />
            <button
              type="button"
              onClick={runScan}
              disabled={scanning}
              data-testid="intervention-scan-ai"
              style={{
                width: "100%", display: "flex", alignItems: "center", gap: 14,
                padding: "16px 18px", borderRadius: 20,
                background: "var(--mf-brand)", color: "#fff",
                border: "none", cursor: scanning ? "not-allowed" : "pointer",
                minHeight: 72, boxShadow: "var(--mf-shadow-strong)",
                textAlign: "left",
              }}
            >
              {scanning ? (
                <div style={{ width: 20, height: 20, border: "3px solid rgba(255,255,255,0.35)", borderTopColor: "#fff", borderRadius: 10, animation: "mf-spin 700ms linear infinite", margin: "0 auto" }} />
              ) : (
                <>
                  <span style={{
                    width: 40, height: 40, borderRadius: 12, background: "rgba(255,255,255,0.15)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    <Sparkles size={22} color="#fff" />
                  </span>
                  <div style={{ flex: 1 }}>
                    <div style={{ color: "#fff", fontSize: 16, fontWeight: 700 }}>Scanner une facture</div>
                    <div style={{ color: "rgba(255,255,255,0.7)", fontSize: 12, marginTop: 2 }}>L'IA remplira le formulaire</div>
                  </div>
                  <ChevronRight size={18} color="rgba(255,255,255,0.6)" />
                </>
              )}
            </button>
          </div>
        )}

        {isCustom && (
          <Field testID="intervention-title" label="Titre du document" value={title} onChange={setTitle} placeholder="Ex : Contrat de location" />
        )}

        {isMaintenance && !lockedCustomTypeId && (
          <>
            <div style={{ fontSize: 13, fontWeight: 600, color: "var(--mf-text-secondary)", marginBottom: 8 }}>Type</div>
            <div style={{
              display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 16,
            }}>
              {INTERVENTION_TYPES.map((t) => {
                const active = !isCustomMaintenance && type === t;
                return (
                  <button
                    key={t}
                    type="button"
                    data-testid={`chip-${t}`}
                    onClick={() => { setType(t); setCustomTypeId(null); }}
                    style={{
                      padding: "8px 14px", borderRadius: 999, fontSize: 13, fontWeight: 600,
                      background: active ? "var(--mf-brand)" : "var(--mf-bg-muted)",
                      color: active ? "#fff" : "var(--mf-text-secondary)",
                      border: `1px solid ${active ? "var(--mf-brand)" : "var(--mf-border)"}`,
                      cursor: "pointer",
                    }}
                  >
                    {RULES_LABELS[t]}
                  </button>
                );
              })}
              {customTypes.map((c) => {
                const active = isCustomMaintenance && customTypeId === c.id;
                return (
                  <button
                    key={c.id}
                    type="button"
                    data-testid={`chip-custom-${c.id}`}
                    onClick={() => { setType("custom_maintenance"); setCustomTypeId(c.id); }}
                    style={{
                      padding: "8px 14px", borderRadius: 999, fontSize: 13, fontWeight: 600,
                      background: active ? "var(--mf-brand)" : "var(--mf-bg-muted)",
                      color: active ? "#fff" : "var(--mf-text-secondary)",
                      border: `1px solid ${active ? "var(--mf-brand)" : "var(--mf-border)"}`,
                      cursor: "pointer",
                    }}
                  >
                    {c.title}
                  </button>
                );
              })}
            </div>
          </>
        )}

        <Field
          testID="intervention-date"
          label={isDocument ? "Date d'émission" : "Date"}
          type="date"
          value={date}
          onChange={setDate}
        />

        <Field testID="intervention-mileage" label="Kilométrage" value={mileage} onChange={setMileage} placeholder="185 000" />

        {isDocument && (
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "var(--mf-text-secondary)", marginBottom: 6 }}>
              Date d'expiration (optionnel)
            </label>
            <div style={{ display: "flex", gap: 8, alignItems: "stretch" }}>
              <input
                type="date"
                value={expirationDate || ""}
                onChange={(e) => setExpirationDate(e.target.value)}
                data-testid="intervention-expiration"
                style={{
                  flex: 1, padding: "12px 14px", borderRadius: 12,
                  background: "var(--mf-bg-card)", border: "1px solid var(--mf-border)",
                  color: "var(--mf-text)", fontSize: 15, fontFamily: "inherit", outline: "none",
                }}
              />
              {expirationDate && (
                <button
                  type="button"
                  onClick={() => setExpirationDate("")}
                  data-testid="intervention-expiration-clear"
                  aria-label="Réinitialiser la date d'expiration"
                  style={{
                    padding: "0 14px", borderRadius: 12,
                    background: "var(--mf-bg-muted)", border: "1px solid var(--mf-border)",
                    color: "var(--mf-text-secondary)", fontSize: 13, fontWeight: 600,
                    cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6,
                    whiteSpace: "nowrap",
                  }}
                >
                  <X size={14} /> Réinitialiser
                </button>
              )}
            </div>
            <div style={{ marginTop: 6, fontSize: 12, color: "var(--mf-text-muted)" }}>
              Si aucune date n'est saisie, aucune alerte d'expiration ne sera générée.
            </div>
          </div>
        )}

        <Field testID="intervention-garage" label="Garage (optionnel)" value={garage} onChange={setGarage} placeholder="Garage du Parc" />
        <Field testID="intervention-amount" label="Montant € (optionnel)" value={amount} onChange={setAmount} placeholder="120.50" />
        <Field testID="intervention-notes" label="Notes (optionnel)" value={notes} onChange={setNotes} placeholder="Filtre à huile changé" multiline />

        <button
          type="button"
          onClick={attachPhoto}
          data-testid="intervention-attach-photo"
          style={{
            width: "100%", display: "inline-flex", alignItems: "center", gap: 8,
            padding: "12px 14px", borderRadius: 12,
            background: "var(--mf-bg-muted)", border: "none", cursor: "pointer",
            color: "var(--mf-brand)", fontWeight: 600, fontSize: 14, marginBottom: 10,
          }}
        >
          <Paperclip size={18} />
          {photo ? "Photo jointe (cliquer pour changer)" : "Joindre une photo (optionnel)"}
        </button>
        {photo && (
          <img src={photo} alt="Aperçu" style={{ width: "100%", maxHeight: 220, objectFit: "cover", borderRadius: 12, marginBottom: 10 }} />
        )}

        {error && <div style={{ color: "var(--mf-urgent-text)", textAlign: "center", marginTop: 8, marginBottom: 8, fontSize: 14 }}>{error}</div>}

        <Button testID="intervention-submit" type="submit" loading={loading} style={{ marginTop: 16 }}>
          Valider l'intervention
        </Button>
      </form>
      <style>{`@keyframes mf-spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
